import styled from 'styled-components/macro';

import Typography from 'components/Typography/Typography';

/** Mobile: 425px and below */
const VIEW_MOBILE = '@media screen and (max-width: 425px)';
/** Tablet: 768px and below */
const VIEW_TABLET = '@media screen and (max-width: 768px)';
/** 992px and above: 2 cards per row */
const VIEW_992 = '@media screen and (min-width: 993px)';

export const CardViewStyled = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  width: 100%;

  ${VIEW_MOBILE} {
    gap: 0.375rem;
  }

  ${VIEW_992} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem 0.75rem;
  }
`;

export const CardStyled = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: ${({ theme }): string => theme.colors.surface};
  border: 1px solid ${({ theme }): string => theme.colors.primary.variant5};
  border-radius: ${({ theme }): string => theme.radius.md};
  cursor: pointer;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ theme }): string => theme.colors.primary.variant6};
    box-shadow: ${({ theme }): string => theme.shadows.variant1};
  }

  ${VIEW_TABLET} {
    padding: 0.625rem 0.875rem;
    gap: 0.375rem;
  }

  ${VIEW_MOBILE} {
    padding: 0.5rem 0.75rem;
    gap: 0.25rem;
  }
`;

export const CardCheckboxWrapStyled = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0.125rem;
  min-width: 42px;
  min-height: 42px;
`;

export const CardContentStyled = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: auto auto auto auto;
  gap: 0 1rem;
  row-gap: 0.5rem;

  ${VIEW_TABLET} {
    gap: 0 0.75rem;
    row-gap: 0.5rem;
  }

  ${VIEW_MOBILE} {
    gap: 0 0.5rem;
    row-gap: 0.375rem;
  }
`;

/** Field cell: label above value for clarity. Grid placement via gridColumn/gridRow. */
export const CardCellStyled = styled.div<{ $col?: number; $row?: number }>`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  overflow: hidden;
  grid-column: ${({ $col = 1 }): number => $col};
  grid-row: ${({ $row = 1 }): number => $row};

  ${VIEW_MOBILE} {
    gap: 0.0625rem;
  }
`;

export const TextLabel = styled.span`
  color: ${({ theme }): string => theme.colors.primary.variant7};
  font-size: ${({ theme }): string => theme.fontSize.xxs};
  font-weight: ${({ theme }): number => theme.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  line-height: 1.2;

  ${VIEW_TABLET} {
    font-size: 0.625rem;
  }

  ${VIEW_MOBILE} {
    font-size: 0.5625rem;
  }
`;

export const TextValueStyled = styled(Typography)`
  font-size: ${({ theme }): string => theme.fontSize.sm};
  font-weight: ${({ theme }): number => theme.fontWeight.normal};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;

  ${VIEW_TABLET} {
    font-size: ${({ theme }): string => theme.fontSize.xs};
  }

  ${VIEW_MOBILE} {
    font-size: ${({ theme }): string => theme.fontSize.xxs};
  }
`;

export const FormStyled = styled.form`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/** Wraps edit-mode inputs. Standardized sizes; labels hidden at tablet/mobile. */
export const CardInputWrapStyled = styled.div`
  width: 100%;
  min-width: 0;
  flex: 1;
  min-height: 32px;

  & .MuiTextField-root,
  & .MuiFormControl-root,
  & .MuiAutocomplete-root,
  & .MuiInputBase-root {
    width: 100% !important;
    min-width: 0;
  }

  & .MuiOutlinedInput-root,
  & .MuiAutocomplete-root .MuiOutlinedInput-root {
    min-height: 32px;
    height: 32px;
  }

  & .MuiInputBase-input {
    font-size: 0.75rem;
    padding: 6px 10px;
    height: 20px;
    box-sizing: border-box;
  }

  & .MuiSelect-select {
    padding: 6px 10px;
    min-height: 20px;
  }

  /* Tablet and mobile: hide floating input labels in edit mode */
  ${VIEW_TABLET} {
    & .MuiInputLabel-root {
      display: none;
    }
    & .MuiInputLabel-shrink {
      display: none;
    }
  }
`;

export const TextRoNumberStyled = styled(Typography)`
  font-size: ${({ theme }): string => theme.fontSize.md};
  font-weight: ${({ theme }): number => theme.fontWeight.semibold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;

  ${VIEW_TABLET} {
    font-size: ${({ theme }): string => theme.fontSize.sm};
  }

  ${VIEW_MOBILE} {
    font-size: ${({ theme }): string => theme.fontSize.xs};
  }
`;
